/* eslint-disable max-lines, sonarjs/cognitive-complexity */
import type { ChangeEvent, KeyboardEvent, MouseEvent, RefObject } from "react";
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import QuickreplyOutlinedIcon from "@mui/icons-material/QuickreplyOutlined";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import TextField, { type TextFieldOwnerState, type TextFieldProps } from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useForkRef } from "@mui/material/utils";
import isNil from "lodash/isNil";
import { useTranslation } from "react-i18next";

import type { Phrase as GraphQLPhrase } from "@waterly/graphql-types";

import {
  filterPhrasesBySearch,
  getPhraseFirstLinePreview,
  replaceSelectionWithPhrase,
  type TextSelection,
} from "./PhraseTextField.utils";

export type Phrase = Pick<GraphQLPhrase, "id" | "name" | "bodyText" | "userId" | "organizationId">;

export type PhraseTextFieldProps = Omit<TextFieldProps, "InputProps" | "onChange" | "value"> & {
  value: string;
  phrases: Phrase[];
  onChange: (value: string) => void;
  onCreateUserPhrase: (input: UserPhraseCreateInput) => Promise<boolean>;
  onOpen?: () => void;
  pickerAnchorRef?: RefObject<HTMLElement | null>;
  savingPhrase: boolean;
  userPhrasesHref: string;
};

export type UserPhraseCreateInput = Pick<Phrase, "bodyText" | "name">;

type PhraseScopeFilter = "all" | "user";
type PreservedSelectionSnapshot = {
  selection: TextSelection;
  selectedText: string;
};
type PendingSelectionRestore = {
  selection: TextSelection;
  finalValue?: string;
};

const PHRASE_PREVIEW_MAX_LENGTH = 120;
const PHRASE_NAME_MAX_LENGTH = 255;
const PHRASE_PICKER_MAX_HEIGHT = 400;
const PHRASE_PICKER_WIDTH = 520;

const PhraseTextField = ({
  value,
  phrases,
  onChange,
  onCreateUserPhrase,
  onOpen,
  pickerAnchorRef,
  savingPhrase,
  disabled,
  fullWidth,
  inputRef,
  slotProps,
  userPhrasesHref,
  ...textFieldProps
}: PhraseTextFieldProps) => {
  const { t } = useTranslation(["common"]);
  const optionsMenuId = useId(); // connects aria-controls on the button to the menu for accessibility

  const textFieldContainerRef = useRef<HTMLDivElement | null>(null);
  const phraseHeaderRef = useRef<HTMLDivElement | null>(null);
  const searchTextRef = useRef<HTMLInputElement | null>(null);
  const textInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const phraseNameInputRef = useRef<HTMLInputElement | null>(null);
  // records the selected range and its exact text before focus moves into the picker
  const preservedSelectionRef = useRef<PreservedSelectionSnapshot>({
    selection: { start: 0, end: 0 },
    selectedText: "",
  });
  /* records location of cursor/selection after closing the picker, as well as
     the what the final value should be after the phrase is inserted */
  const pendingSelectionRestoreRef = useRef<PendingSelectionRestore | null>(null);
  const mergedInputRef = useForkRef(inputRef, textInputRef);

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverExited, setPopoverExited] = useState(true);
  const [phraseButtonFilter, setPhraseButtonFilter] = useState<PhraseScopeFilter>("all");
  const [searchText, setSearchText] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [saveNewPhraseUIEnabled, setSaveNewPhraseUIEnabled] = useState(false);
  const [phraseName, setPhraseName] = useState("");
  const [optionsAnchor, setOptionsAnchor] = useState<HTMLElement | null>(null);

  const selectedText = preservedSelectionRef.current.selectedText;
  const hasSelectedText = selectedText.length > 0;

  const allUserPhrases = useMemo(() => phrases.filter((phrase) => phrase.userId != null) ?? [], [phrases]);
  const allOrganizationPhrases = useMemo(
    () => phrases.filter((phrase) => phrase.organizationId != null) ?? [],
    [phrases],
  );
  const filteredUserPhrases = useMemo(
    () => filterPhrasesBySearch(allUserPhrases, searchText),
    [allUserPhrases, searchText],
  );
  const filteredOrganizationPhrases = useMemo(
    () => (phraseButtonFilter === "all" ? filterPhrasesBySearch(allOrganizationPhrases, searchText) : []),
    [phraseButtonFilter, searchText, allOrganizationPhrases],
  );
  const filteredPhrases = useMemo(
    () => [...filteredUserPhrases, ...filteredOrganizationPhrases],
    [filteredUserPhrases, filteredOrganizationPhrases],
  );
  const hasSearchablePhrases =
    allUserPhrases.length > 0 || (phraseButtonFilter === "all" && allOrganizationPhrases.length > 0);

  const normalizedPhraseName = phraseName.trim().toLowerCase();
  const phraseNameAlreadyExists =
    normalizedPhraseName.length > 0 &&
    allUserPhrases.some((phrase) => phrase.name.trim().toLowerCase() === normalizedPhraseName);

  const handleTextFieldChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange(event.target.value);
    },
    [onChange],
  );

  const captureInputSelection = useCallback(() => {
    const input = textInputRef.current;
    if (isNil(input?.selectionStart) || isNil(input.selectionEnd)) return;

    preservedSelectionRef.current = {
      selection: {
        start: input.selectionStart,
        end: input.selectionEnd,
      },
      selectedText: input.value.slice(input.selectionStart, input.selectionEnd),
    };
  }, []);

  const handleOpenPopover = useCallback(() => {
    captureInputSelection();
    setPhraseButtonFilter("all");
    setSearchText("");
    setSaveNewPhraseUIEnabled(false);
    setPhraseName("");
    setPopoverExited(false);
    setPopoverOpen(true);
    onOpen?.();
  }, [captureInputSelection, onOpen]);

  const handleOpenOptions = useCallback(() => {
    setOptionsAnchor(phraseHeaderRef.current);
  }, []);

  const handleCloseOptions = useCallback(() => {
    setOptionsAnchor(null);
  }, []);

  const handleClosePopover = useCallback(() => {
    pendingSelectionRestoreRef.current = { selection: preservedSelectionRef.current.selection };
    setOptionsAnchor(null);
    setPopoverOpen(false);
  }, []);

  const handleAllFilter = useCallback(() => {
    setPhraseButtonFilter("all");
  }, []);

  const handleUserFilter = useCallback(() => {
    setPhraseButtonFilter("user");
  }, []);

  const handleSearchTextChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  }, []);

  const handleOpenSaveSelection = useCallback(() => {
    setSaveNewPhraseUIEnabled(true);
  }, []);

  const handlePhraseNameChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setPhraseName(event.target.value);
  }, []);

  const handleCancelSaveSelection = useCallback(() => {
    setSaveNewPhraseUIEnabled(false);
    setPhraseName("");
  }, []);

  const createUserPhrase = useCallback(async () => {
    const trimmedName = phraseName.trim();
    if (!trimmedName || phraseNameAlreadyExists) return;

    const success = await onCreateUserPhrase({ bodyText: selectedText, name: trimmedName });
    if (success) {
      setSaveNewPhraseUIEnabled(false);
      setPhraseName("");
      setSearchText("");
    }
  }, [onCreateUserPhrase, phraseName, phraseNameAlreadyExists, selectedText]);

  const handleSaveSelection = useCallback(() => {
    void createUserPhrase();
  }, [createUserPhrase]);

  const handlePhraseListMouseEnter = useCallback((event: MouseEvent<HTMLElement>) => {
    setHighlightedIndex(Number(event.currentTarget.dataset.phraseIndex));
  }, []);

  const handlePhraseSelection = useCallback(
    (phrase: Phrase) => {
      const replacement = replaceSelectionWithPhrase(
        value,
        preservedSelectionRef.current.selection,
        phrase.bodyText,
      );
      const caretPositionAfterInsertion = {
        start: replacement.caretPosition,
        end: replacement.caretPosition,
      };

      preservedSelectionRef.current = {
        selection: caretPositionAfterInsertion,
        selectedText: "",
      };
      pendingSelectionRestoreRef.current = {
        selection: caretPositionAfterInsertion,
        finalValue: replacement.value,
      };
      onChange(replacement.value);
      setPopoverOpen(false);
    },
    [onChange, value],
  );

  const handlePhraseListClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      const phrase = filteredPhrases[Number(event.currentTarget.dataset.phraseIndex)];
      if (phrase) handlePhraseSelection(phrase);
    },
    [filteredPhrases, handlePhraseSelection],
  );

  const handleSearchKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        if (!filteredPhrases.length) return;

        setHighlightedIndex((currentIndex) => {
          if (event.key === "ArrowDown") return (currentIndex + 1) % filteredPhrases.length;
          return currentIndex <= 0 ? filteredPhrases.length - 1 : currentIndex - 1;
        });

        return;
      }

      if (event.key === "Enter") {
        const highlightedPhrase = filteredPhrases[highlightedIndex];
        if (!highlightedPhrase) return;

        event.preventDefault();
        handlePhraseSelection(highlightedPhrase);
        return;
      }

      if (event.key !== "Escape") return;

      event.preventDefault();
      event.stopPropagation();
      if (searchText) {
        setSearchText("");
        return;
      }

      handleClosePopover();
    },
    [filteredPhrases, handleClosePopover, handlePhraseSelection, highlightedIndex, searchText],
  );

  const handlePopoverEntered = useCallback(() => {
    searchTextRef.current?.focus();
  }, []);

  const handlePopoverExited = useCallback(() => {
    setPopoverExited(true);
  }, []);

  const highlightedPhrase = filteredPhrases[highlightedIndex];
  const phraseListboxId = useId();
  const highlightedPhraseOptionId = highlightedPhrase
    ? `${phraseListboxId}-option-${highlightedPhrase.id}`
    : undefined;

  const renderPhraseGroup = (phrases: Phrase[], label: string, startIndex: number) => {
    if (!phrases.length) return null;

    return (
      <Box role="group" aria-label={label}>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ display: "block", lineHeight: 1.5, px: 2, pt: 1.5, pb: 0.5 }}
        >
          {label}
        </Typography>

        {phrases.map((phrase, index) => {
          const phraseIndex = startIndex + index;
          return (
            <ListItemButton
              key={phrase.id}
              id={`${phraseListboxId}-option-${phrase.id}`}
              role="option"
              component="div"
              data-phrase-index={phraseIndex}
              selected={phraseIndex === highlightedIndex}
              aria-selected={phraseIndex === highlightedIndex}
              onClick={handlePhraseListClick}
              onMouseEnter={handlePhraseListMouseEnter}
              sx={{ alignItems: "flex-start", flexDirection: "column", px: 2, py: 0.75 }}
            >
              <Typography variant="subtitle2" fontWeight="bold" noWrap width="100%">
                {phrase.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap width="100%">
                {getPhraseFirstLinePreview(phrase.bodyText, PHRASE_PREVIEW_MAX_LENGTH)}
              </Typography>
            </ListItemButton>
          );
        })}
      </Box>
    );
  };

  const resolveInputSlotProps = useCallback(
    (ownerState: TextFieldOwnerState) => {
      const inputSlotProps =
        typeof slotProps?.input === "function" ? slotProps.input(ownerState) : slotProps?.input;

      return {
        ...inputSlotProps,
        endAdornment: (
          <>
            {inputSlotProps?.endAdornment}
            <InputAdornment position="end" sx={{ alignSelf: ownerState.multiline ? "flex-end" : undefined }}>
              <Tooltip title={t("common:insert_phrase")}>
                <span>
                  <IconButton
                    aria-label={t("common:insert_phrase")}
                    disabled={disabled}
                    edge="end"
                    size="small"
                    onClick={handleOpenPopover}
                  >
                    <QuickreplyOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </InputAdornment>
          </>
        ),
      };
    },
    [disabled, handleOpenPopover, slotProps, t],
  );

  useEffect(() => {
    setHighlightedIndex(filteredPhrases.length ? 0 : -1);
  }, [phraseButtonFilter, searchText, filteredPhrases.length]);

  useEffect(() => {
    if (saveNewPhraseUIEnabled) phraseNameInputRef.current?.focus();
  }, [saveNewPhraseUIEnabled]);

  // restores focus to the textField and restores the caret/selection after the picker closes
  useLayoutEffect(() => {
    const pendingRestore = pendingSelectionRestoreRef.current;
    if (
      popoverOpen ||
      !popoverExited ||
      !pendingRestore ||
      (pendingRestore.finalValue !== undefined && pendingRestore.finalValue !== value)
    ) {
      return;
    }

    const input = textInputRef.current;
    input?.focus();
    input?.setSelectionRange(pendingRestore.selection.start, pendingRestore.selection.end);
    pendingSelectionRestoreRef.current = null;
  }, [popoverExited, popoverOpen, value]);

  const externalPickerAnchor = pickerAnchorRef?.current;
  const pickerAnchor = externalPickerAnchor ?? textFieldContainerRef.current;
  const pickerWidth = pickerAnchor?.clientWidth;

  return (
    <>
      <Box
        ref={textFieldContainerRef}
        sx={{ display: fullWidth ? "block" : "inline-block", width: fullWidth ? "100%" : undefined }}
      >
        <TextField
          {...textFieldProps}
          disabled={disabled}
          fullWidth={fullWidth}
          inputRef={mergedInputRef}
          slotProps={{ ...slotProps, input: resolveInputSlotProps }}
          value={value}
          onChange={handleTextFieldChange}
        />
      </Box>
      <Popover
        open={popoverOpen}
        anchorEl={pickerAnchor}
        onClose={handleClosePopover}
        disableAutoFocus
        disableRestoreFocus
        anchorOrigin={{ vertical: "bottom", horizontal: externalPickerAnchor ? "left" : "center" }}
        transformOrigin={{ vertical: "top", horizontal: externalPickerAnchor ? "left" : "center" }}
        slotProps={{
          paper: {
            sx: {
              display: "flex",
              flexDirection: "column",
              maxHeight: PHRASE_PICKER_MAX_HEIGHT,
              maxWidth: "calc(100vw - 32px)",
              width: pickerWidth && pickerWidth > 0 ? pickerWidth : PHRASE_PICKER_WIDTH,
            },
          },
          transition: { onEntered: handlePopoverEntered, onExited: handlePopoverExited },
        }}
      >
        <Stack
          ref={phraseHeaderRef}
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            p: 1,
            "& > .MuiIconButton-root": { ml: 0 },
          }}
        >
          <Chip
            clickable
            color={phraseButtonFilter === "all" ? "primary" : "default"}
            label={t("common:phrase_filter_all")}
            size="small"
            sx={{ height: 28 }}
            onClick={handleAllFilter}
          />
          <Chip
            clickable
            color={phraseButtonFilter === "user" ? "primary" : "default"}
            label={t("common:phrase_filter_my")}
            size="small"
            sx={{ height: 28 }}
            onClick={handleUserFilter}
          />

          <TextField
            fullWidth
            hiddenLabel
            inputRef={searchTextRef}
            placeholder={t("common:search_phrases")}
            size="small"
            value={searchText}
            onChange={handleSearchTextChange}
            onKeyDown={handleSearchKeyDown}
            slotProps={{
              htmlInput: {
                "aria-activedescendant": highlightedPhraseOptionId,
                "aria-autocomplete": "list",
                "aria-controls": phraseListboxId,
                "aria-expanded": popoverOpen,
                role: "combobox",
                "aria-label": t("common:search_phrases"),
                autoComplete: "off",
                sx: { fontSize: "0.875rem", py: 0.5 },
              },
              input: {
                sx: { borderRadius: "24px", height: 32 },
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <IconButton
            aria-label={t("common:options")}
            aria-haspopup="menu"
            aria-controls={optionsAnchor ? optionsMenuId : undefined}
            aria-expanded={Boolean(optionsAnchor)}
            size="small"
            sx={{ flexShrink: 0 }}
            onClick={handleOpenOptions}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            id={optionsMenuId}
            anchorEl={optionsAnchor}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            open={Boolean(optionsAnchor)}
            onClose={handleCloseOptions}
            slotProps={{ list: { sx: { py: 0.5 } } }}
          >
            <MenuItem
              dense
              component="a"
              href={userPhrasesHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleCloseOptions}
              sx={{ fontSize: "0.875rem", "& .MuiListItemIcon-root": { minWidth: 28 } }}
            >
              <ListItemIcon>
                <OpenInNewIcon sx={{ fontSize: 18 }} />
              </ListItemIcon>
              {t("common:manage")}
            </MenuItem>
          </Menu>
        </Stack>
        <Box id={phraseListboxId} role="listbox" sx={{ minHeight: 80, overflowY: "auto", pb: 1 }}>
          {filteredPhrases.length ? (
            <>
              {renderPhraseGroup(filteredUserPhrases, t("common:my_phrases"), 0)}
              {renderPhraseGroup(
                filteredOrganizationPhrases,
                t("common:organization_phrases"),
                filteredUserPhrases.length,
              )}
            </>
          ) : (
            <Typography color="text.secondary" textAlign="center" sx={{ px: 2, py: 3 }}>
              {t(
                searchText.trim() && hasSearchablePhrases
                  ? "common:no_phrases_match"
                  : "common:phrase_list_empty_state",
              )}
            </Typography>
          )}
        </Box>
        <Box sx={{ borderTop: 1, borderColor: "divider" }}>
          {saveNewPhraseUIEnabled ? (
            <>
              <Box display="flex" width="100%" justifyContent="center">
                <Typography my={1}>{t("common:add_phrase")}</Typography>
              </Box>
              <Stack spacing={0.75} sx={{ px: 2, py: 0.75 }}>
                <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                  <Stack flex={1} minWidth={0} spacing={1}>
                    <TextField
                      fullWidth
                      required
                      hiddenLabel
                      error={phraseNameAlreadyExists}
                      helperText={
                        phraseNameAlreadyExists ? t("common:phrase_name_already_exists") : undefined
                      }
                      inputRef={phraseNameInputRef}
                      placeholder={t("common:phrase_name")}
                      size="small"
                      slotProps={{
                        htmlInput: {
                          "aria-label": t("common:phrase_name"),
                          maxLength: PHRASE_NAME_MAX_LENGTH,
                        },
                        input: {
                          sx: { fontSize: "0.875rem", height: 34 },
                        },
                      }}
                      value={phraseName}
                      onChange={handlePhraseNameChange}
                    />
                  </Stack>
                  <Button
                    size="small"
                    disabled={savingPhrase}
                    sx={{ mt: 0.25 }}
                    onClick={handleCancelSaveSelection}
                  >
                    {t("common:cancel")}
                  </Button>
                  <Button
                    size="small"
                    disabled={savingPhrase || !phraseName.trim() || phraseNameAlreadyExists}
                    startIcon={savingPhrase ? <CircularProgress size={14} /> : undefined}
                    sx={{ mt: 0.25 }}
                    variant="contained"
                    onClick={handleSaveSelection}
                  >
                    {t("common:save")}
                  </Button>
                </Stack>
                <Typography ml={2} color="text.secondary" fontStyle="italic" noWrap variant="caption">
                  {`${t("common:phrase_text")} “${selectedText}”`}
                </Typography>
              </Stack>
            </>
          ) : (
            <Button
              fullWidth
              disabled={!hasSelectedText}
              size="small"
              startIcon={<NoteAddOutlinedIcon fontSize="small" />}
              sx={{ borderRadius: 0, justifyContent: "flex-start", px: 2, py: 0.75, textTransform: "none" }}
              onClick={handleOpenSaveSelection}
            >
              {hasSelectedText
                ? t("common:save_selection_as_phrase")
                : t("common:select_text_to_save_phrase")}
            </Button>
          )}
        </Box>
        <Typography
          color="text.disabled"
          variant="caption"
          sx={{ borderTop: 1, borderColor: "divider", fontSize: "0.6875rem", px: 2, py: 0.5 }}
        >
          {t("common:phrase_picker_keyboard_instructions")}
        </Typography>
      </Popover>
    </>
  );
};

export default PhraseTextField;
